import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authedFileHref } from '../utils/files';

/**
 * Settings → Documents (sales rework Phase 2): the provided-documents library.
 * Terms & Conditions sets and pre-install guides, keyed kind × set × region.
 * Product Configuration points a product type at a T&C set; the regional
 * variant is chosen at pack/email generation time.
 */

const KIND_LABELS = {
  terms_and_conditions: 'Terms & Conditions',
  pre_install_guide: 'Pre-Install Guides'
};

const REGIONS = ['GB', 'IE', 'EU', 'US'];

function DocumentsSettings() {
  const { authFetch, user } = useAuth();
  const canEdit = user?.role === 'admin';

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ kind: 'terms_and_conditions', set_key: '', region: 'GB', label: '', file: null, fileName: '' });

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/v1/document-library');
      if (!response.ok) throw new Error('Failed to load the document library');
      const data = await response.json();
      setDocuments(data.data || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, file: reader.result, fileName: file.name }));
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!form.set_key.trim() || !form.label.trim() || !form.file) {
      setMessage('Set, label and a PDF file are required');
      return;
    }
    try {
      setUploading(true);
      setMessage('');
      const response = await authFetch('/api/v1/document-library', {
        method: 'POST',
        body: JSON.stringify({
          kind: form.kind,
          set_key: form.set_key,
          region: form.region,
          label: form.label,
          file: form.file,
          name: form.fileName
        })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Upload failed');
      }
      setForm({ kind: form.kind, set_key: '', region: 'GB', label: '', file: null, fileName: '' });
      setMessage('Document uploaded');
      fetchDocuments();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setUploading(false);
    }
  };

  const deactivate = async (doc) => {
    try {
      const response = await authFetch(`/api/v1/document-library/${doc.document_id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to deactivate document');
      fetchDocuments();
    } catch (error) {
      setMessage(error.message);
    }
  };

  if (loading) return <div className="settings-loading">Loading documents…</div>;

  return (
    <div className="documents-settings">
      <h2>Documents</h2>
      <p className="section-desc">
        Provided static documents used in customer packs. T&amp;C sets are selected per product type
        in Product Configuration; the regional variant is picked when a pack is generated.
      </p>
      {message && <div className="settings-message">{message}</div>}

      {canEdit && (
        <div className="document-upload-form">
          <h3>Upload document</h3>
          <div className="form-row compact">
            <div className="form-group">
              <label>Kind</label>
              <select value={form.kind} onChange={(e) => setForm(prev => ({ ...prev, kind: e.target.value }))}>
                <option value="terms_and_conditions">Terms &amp; Conditions</option>
                <option value="pre_install_guide">Pre-Install Guide</option>
              </select>
            </div>
            <div className="form-group">
              <label>Set key</label>
              <input
                value={form.set_key}
                placeholder="e.g. standard"
                onChange={(e) => setForm(prev => ({ ...prev, set_key: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Region</label>
              <select value={form.region} onChange={(e) => setForm(prev => ({ ...prev, region: e.target.value }))}>
                {REGIONS.map(region => <option key={region} value={region}>{region}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row compact">
            <div className="form-group">
              <label>Label</label>
              <input
                value={form.label}
                placeholder="e.g. Standard UK Terms & Conditions"
                onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>File (PDF)</label>
              <input type="file" accept="application/pdf" onChange={(e) => handleFile(e.target.files?.[0])} />
              {form.fileName && <small className="field-hint">{form.fileName}</small>}
            </div>
            <div className="form-group document-upload-action">
              <button className="btn-primary" disabled={uploading} onClick={upload}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {Object.entries(KIND_LABELS).map(([kind, label]) => {
        const rows = documents.filter(doc => doc.kind === kind);
        return (
          <div key={kind} className="document-kind-section">
            <h3>{label}</h3>
            {rows.length === 0 ? (
              <p className="field-hint">No documents uploaded yet.</p>
            ) : (
              <table className="pricing-schedule-table">
                <thead>
                  <tr>
                    <th>Set</th>
                    <th>Region</th>
                    <th>Label</th>
                    <th>File</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(doc => (
                    <tr key={doc.document_id} className={doc.active ? '' : 'stage-inactive'}>
                      <td>{doc.set_key}</td>
                      <td>{doc.region}</td>
                      <td>{doc.label}</td>
                      <td>
                        {doc.attachment?.url ? (
                          <a href={authedFileHref(doc.attachment.url)} target="_blank" rel="noreferrer">
                            {doc.attachment.name || 'View PDF'}
                          </a>
                        ) : '—'}
                      </td>
                      <td>{doc.active ? 'Active' : 'Inactive'}</td>
                      <td>
                        {canEdit && doc.active && (
                          <button className="btn-danger btn-small" onClick={() => deactivate(doc)}>Deactivate</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default DocumentsSettings;
